import { useGetDynamicDomainLink } from '@hooks/use-get-dynamic-domain-link'
import { useParameter } from '@hooks/use-parameter'
import { useTrackExternalLink } from '@hooks/use-track-external-link'
import { useTrackNavigationLink } from '@hooks/use-track-navigation-link'
import { useLocation } from '@reach/router'
import { type EnhancedComponentExtendedProps } from '@ui/enhanced-components/types'
import { type GatsbyLinkProps, Link as GatsbyLink } from 'gatsby'
import queryString from 'query-string'
import React, {
  type ComponentType,
  type ElementType,
  type FC,
  type SyntheticEvent,
  useCallback,
} from 'react'

import { cleanUrl } from './clean-url'
import { isExternal } from './is-external'
import { isIosWebview } from './is-webview'
import { isMarketLink, isPrivateLink, isSubscriptionLink } from './links'
import { SupportedQueryParams } from './parameters'
import { scrollToHash } from './scroll-to-hash'

type DataDomainAttribute = {
  'data-domain'?: Utils.UndefOrNull<string>
}

export type HTMLAttributesExtended = Partial<GatsbyLinkProps<any>> & {
  component?: ElementType
  href?: string
}

export const withLinkProps =
  <EnhancedComponentProps extends EnhancedComponentExtendedProps>(
    Component: ComponentType<EnhancedComponentProps>
  ): FC<EnhancedComponentProps & HTMLAttributesExtended> =>
  (props) => {
    const { origin, pathname, hash } = useLocation()
    const { href, onClick } = props
    const trackExternalHyperlink = useTrackExternalLink()
    const trackNavigationLink = useTrackNavigationLink()
    const parameterOrigin = useParameter(SupportedQueryParams.Origin)
    const getDynamicMarketLink = useGetDynamicDomainLink('market')
    const getDynamicPrivateLink = useGetDynamicDomainLink('private')

    const onExternalLinkClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        trackExternalHyperlink(event)

        if (onClick) {
          onClick(event)
        }
      },
      [trackExternalHyperlink, onClick]
    )

    // If same url and hash we scroll to it because shouldUpdateScroll won't work on same pathname and hash
    const onScrollToHash = useCallback(
      (event: SyntheticEvent) => {
        const { pathname: linkPathname, hash: linkHash } = new URL(href, origin)
        if (pathname === linkPathname && hash === linkHash) {
          event.preventDefault()
          scrollToHash(hash)
        }
      },
      [href, origin, pathname, hash]
    )

    const onNavigationLinkClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        trackNavigationLink(event)

        if (onClick) {
          onClick(event)
        }

        onScrollToHash(event)
      },
      [trackNavigationLink, onClick, onScrollToHash]
    )

    const newProps: HTMLAttributesExtended & DataDomainAttribute = {}
    if (href) {
      const fixedHref = cleanUrl(href)
      const isExternalHref = isExternal(fixedHref)

      if (isExternalHref) {
        // Fix bug des liens non cliquables dans la webview ios (en SSR)
        if (!isIosWebview()) {
          newProps.target = '_blank'
        }

        if (isSubscriptionLink(fixedHref)) {
          // Append param origin if it got saved on load
          const query = parameterOrigin
            ? { [SupportedQueryParams.Origin]: parameterOrigin }
            : {}
          newProps.href = queryString.stringifyUrl({
            url: fixedHref,
            query,
          })
          newProps.rel = 'nofollow'
          newProps['data-domain'] = 'subscription'
        } else if (isPrivateLink(fixedHref)) {
          newProps.href = getDynamicPrivateLink(fixedHref)
          newProps.rel = 'nofollow'
          newProps['data-domain'] = 'private'
        } else if (isMarketLink(fixedHref)) {
          newProps.href = getDynamicMarketLink(fixedHref)
          newProps['data-domain'] = 'market'
        } else {
          newProps.href = fixedHref
          newProps['data-domain'] = null
        }

        newProps.component = 'a'
        newProps.onClick = onExternalLinkClick
      } else {
        newProps.to = fixedHref
        newProps.component = GatsbyLink
        newProps.onClick = onNavigationLinkClick
      }
    } else {
      // TODO add tracking on basic button/link
    }

    return <Component {...props} {...newProps} />
  }
